import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  demoActivityQuery,
  demoScenariosQuery,
  demosOffered,
  type DemoActivity,
  type DemoScenario,
} from '../api/demos'
import { meQuery } from '../api/jedi'
import { todayIn, usePersonZone } from '../lib/personTime'
import { AdminTabs } from '../shell/AdminTabs'
import { demoDateWords } from '../shell/demoDates'
import { DemoEngagements, useOpenDemo } from '../shell/DemoEngagements'
import { DemoSetupForm, DemoSetupProgress, versionWords, wordFor } from '../shell/DemoSetup'
import { LoadProblem } from '../shell/LoadProblem'

/**
 * What the page is doing, held in the URL rather than in the component:
 * `scenario` is the set-up form for one scenario, and `watch` is a setup being
 * built. The URL survives what a component's state does not, and the first
 * demo in a fresh deployment is exactly that case: the moment it is ready the
 * person is on an engagement for the first time, the shell gains its rail and
 * bar, and everything under it mounts again.
 */
type DemosSearch = { scenario?: string; watch?: string }

export const Route = createFileRoute('/_shell/admin_/demos')({
  component: DemosGate,
  validateSearch: (search: Record<string, unknown>): DemosSearch => ({
    ...(typeof search.scenario === 'string' && search.scenario ? { scenario: search.scenario } : {}),
    ...(typeof search.watch === 'string' && search.watch ? { watch: search.watch } : {}),
  }),
})

/**
 * Administration › Demos (ADR-0063 decision 10, from Erik's demo-environment
 * rulings, 2026-10-22): set up a demo engagement from a scenario this build
 * carries, dated for the day it will be presented, then reset it, delete it,
 * and fetch its presenter's kit. `docs/runbooks/demo-operations.md` is how a
 * presenter uses it.
 *
 * The gate is COSMETIC, as Administration's is. Every route this page calls
 * answers 404 where the deployment does not offer demos and refuses anyone
 * without the EngagementAdmin app role; this only keeps a page that would be
 * refused out of the way of the people it would refuse.
 */
function DemosGate() {
  const me = useQuery(meQuery)
  if (me.isPending) return null
  if (!demosOffered(me.data)) {
    return (
      <section className="surface">
        <header className="surface-head">
          <h1>Demos</h1>
          <p className="surface-sub">Demos are not available here.</p>
        </header>
      </section>
    )
  }
  return <Demos />
}

function Demos() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const scenarios = useQuery(demoScenariosQuery)
  const openDemo = useOpenDemo()
  const [openSaid, setOpenSaid] = useState<string | null>(null)
  const stage = useRef<HTMLDivElement>(null)

  // Each change of stage replaces the last, so Back leaves the page rather
  // than returning to a form that has already been sent.
  const go = (next: DemosSearch) => {
    setOpenSaid(null)
    void navigate({ search: next, replace: true })
  }

  // The stage sits above the list a reset is pressed from, so it is brought
  // into view when it changes.
  useEffect(() => {
    if (search.scenario || search.watch) stage.current?.scrollIntoView({ block: 'nearest' })
  }, [search.scenario, search.watch])

  const chosen = search.scenario ? scenarios.data?.find((s) => s.id === search.scenario) : undefined

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <h1>Administration</h1>
        <p className="surface-sub">Set up, reset and delete demo engagements.</p>
        <AdminTabs />
      </header>

      <h2 className="surface-h2">Scenarios</h2>
      {scenarios.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={scenarios.data ? null : scenarios.error} />
      {scenarios.data?.length === 0 && <p className="surface-note">No scenarios yet.</p>}
      {scenarios.data && scenarios.data.length > 0 && (
        <ul className="demo-scenarios">
          {scenarios.data.map((scenario) => (
            <li key={scenario.id} className="demo-scenario">
              <div className="demo-scenario-text">
                <p className="demo-scenario-title">
                  {scenario.title} <span className="demo-version">{versionWords(scenario.version)}</span>
                </p>
                <p className="demo-scenario-summary">{scenario.summary}</p>
              </div>
              <button
                type="button"
                className="dialog-primary"
                aria-label={`Set up ${scenario.title}`}
                onClick={() => go({ scenario: scenario.id })}
              >
                Set up
              </button>
            </li>
          ))}
        </ul>
      )}

      <div ref={stage}>
        {(chosen || search.watch) && <h2 className="surface-h2">Set up</h2>}
        {search.watch ? (
          <DemoSetupProgress
            key={search.watch}
            setupId={search.watch}
            onOpen={async (engagement) => {
              setOpenSaid(null)
              if (!(await openDemo(engagement))) setOpenSaid(`You are not on ${engagement.name}.`)
            }}
            onClose={() => go({})}
          />
        ) : chosen ? (
          <DemoSetupForm
            key={chosen.id}
            scenario={chosen}
            onCancel={() => go({})}
            onStarted={(accepted) => go({ watch: accepted.setupId })}
          />
        ) : null}
        {openSaid && <p className="admin-ok" aria-live="polite">{openSaid}</p>}
      </div>

      <h2 className="surface-h2">Demo engagements</h2>
      <DemoEngagements
        scenarios={scenarios.data ?? []}
        onReset={(accepted) => go({ watch: accepted.setupId })}
      />

      <h2 className="surface-h2">Activity</h2>
      <DemoActivityTable scenarios={scenarios.data ?? []} />
    </section>
  )
}

const ACTION_WORDS: Record<string, string> = {
  setup: 'Set up',
  'set-up': 'Set up',
  reset: 'Reset',
  delete: 'Delete',
}

/** Every setup, reset and delete, newest first: who, when, which scenario and date, and how it ended. */
function DemoActivityTable({ scenarios }: { scenarios: DemoScenario[] }) {
  const zone = usePersonZone()
  const today = todayIn(zone)
  const activity = useQuery(demoActivityQuery)

  if (activity.isPending) return <p className="surface-note">Loading…</p>
  if (!activity.data) return <LoadProblem error={activity.error} />
  if (activity.data.length === 0) return <p className="surface-note">Nothing set up, reset or deleted yet.</p>

  const rows = [...activity.data].sort((a, b) => b.at.localeCompare(a.at))
  return (
    <div className="demo-table-wrap">
      <table className="demo-table demo-activity">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Who</th>
            <th scope="col">What</th>
            <th scope="col">Scenario</th>
            <th scope="col">Story date</th>
            <th scope="col">Engagement</th>
            <th scope="col">Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry: DemoActivity, index) => (
            <tr key={`${entry.at}-${index}`} className="demo-row">
              <td className="demo-mono">{momentWords(entry.at, zone)}</td>
              <td>{entry.who}</td>
              <td>{ACTION_WORDS[entry.action] ?? wordFor(entry.action)}</td>
              <td>
                {scenarios.find((s) => s.id === entry.scenarioId)?.title ?? entry.scenarioId}{' '}
                <span className="demo-mono demo-cell-sub demo-inline">{versionWords(entry.version)}</span>
              </td>
              <td>{entry.demoDate ? demoDateWords(entry.demoDate.slice(0, 10), today) : ''}</td>
              <td className="demo-mono">{entry.engagementId}</td>
              <td>{wordFor(entry.result)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** "22 Oct, 3:04 PM" in the person's zone: when something happened, at a glance. */
function momentWords(at: string, zone: string): string {
  return new Date(at).toLocaleString(undefined, {
    timeZone: zone, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  })
}
